import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { MaterialIcons, FontAwesome6 } from '@expo/vector-icons';

import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interMedium, interSemiBold } from '../../lib/fonts';
import Button from '../ui/Button';
import { PaperActionsProps, PaperActionConfig, PaperSectionLink } from './types';

function renderActionIcon(action: PaperActionConfig) {
    const iconColor = action.variant === 'accent' ? '#ffffff' : colors.text700;

    if (action.iconFamily === 'fontawesome') {
        return <FontAwesome6 name={action.icon as any} size={14} color={iconColor} />;
    }

    return <MaterialIcons name={action.icon as any} size={18} color={iconColor} />;
}

function ActionChip({
    action,
}: {
    action: PaperActionConfig;
}) {
    const { fontsLoaded } = useFontLoading();

    if (action.variant === 'primary') {
        return (
            <Button
                label={action.label}
                onPress={action.onPress}
                variant="primary"
                disabled={action.disabled}
                testID={action.testID}
            />
        );
    }

    return (
        <TouchableOpacity
            style={[
                styles.secondaryAction,
                action.variant === 'accent' && styles.accentAction,
            ]}
            onPress={action.onPress}
            disabled={action.disabled}
            activeOpacity={0.78}
            testID={action.testID}
        >
            {renderActionIcon(action)}
            <Text
                style={[
                    styles.secondaryActionText,
                    action.variant === 'accent' && styles.accentActionText,
                    fontsLoaded && interSemiBold,
                ]}
            >
                {action.label}
            </Text>
        </TouchableOpacity>
    );
}

function SectionLinkChip({
    link,
    onPress,
}: {
    link: PaperSectionLink;
    onPress?: (id: PaperSectionLink['id']) => void;
}) {
    const { fontsLoaded } = useFontLoading();

    return (
        <TouchableOpacity
            style={styles.sectionChip}
            onPress={() => onPress?.(link.id)}
            activeOpacity={0.76}
            testID={`paper-section-link-${link.id}`}
        >
            <Text style={[styles.sectionChipText, fontsLoaded && interMedium]}>
                {link.label}
            </Text>
        </TouchableOpacity>
    );
}

export default function PaperActions({
    primaryAction = null,
    secondaryActions = [],
    sectionLinks = [],
    onSectionPress,
}: PaperActionsProps) {
    return (
        <View style={styles.container}>
            {(primaryAction || secondaryActions.length > 0) ? (
                <View style={styles.actionsWrap}>
                    {primaryAction ? (
                        primaryAction.variant === 'primary' ? (
                            <View style={styles.primaryActionWrap}>
                                <ActionChip action={primaryAction} />
                            </View>
                        ) : (
                            <ActionChip action={primaryAction} />
                        )
                    ) : null}

                    {secondaryActions.map((action) => (
                        <ActionChip key={action.id} action={action} />
                    ))}
                </View>
            ) : null}

            {sectionLinks.length > 0 ? (
                <View style={styles.sectionLinksWrap}>
                    {sectionLinks.map((link) => (
                        <SectionLinkChip
                            key={link.id}
                            link={link}
                            onPress={onSectionPress}
                        />
                    ))}
                </View>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 14,
    },
    actionsWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
    },
    primaryActionWrap: {
        minWidth: 220,
        maxWidth: '100%',
    },
    secondaryAction: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 46,
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 12,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.18s ease',
            ':hover': {
                borderColor: '#ccd7ea',
                backgroundColor: '#fbfcff',
            },
        }),
    },
    secondaryActionText: {
        fontSize: 14,
        color: colors.text900,
    },
    accentAction: {
        backgroundColor: colors.action,
        borderColor: colors.action,
    },
    accentActionText: {
        color: '#ffffff',
    },
    sectionLinksWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    sectionChip: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#edf2f7',
        borderWidth: 1,
        borderColor: '#d8e0ec',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    sectionChipText: {
        fontSize: 12,
        color: colors.text700,
    },
});
